import { useEffect, useMemo, useRef, useState } from "react"

import { FileOutlined } from "@ant-design/icons"
import { Button, Radio } from "antd"
import { assetsActions } from "features/assets/model/slice/assetsSlice"
import { getFileVersionsItemsSelector } from "features/file-versions/model/selectors/fileVersionsSelectors"
import { fetchFileVersionsThunk, fetchSharedFileVersionsThunk, fileVersionsActions, restoreFileVersionThunk, restoreSharedFileVersionThunk } from "features/file-versions/model/slice/fileVersionsSlice"
import { UPLOADER_LIST_TYPES, uploadFilesThunk } from "features/files-uploading/model/slice/filesUploadingSlice"
import { useGetEnablePermission } from "features/public-link-files/hooks/useGetEnablePermission"
import { checkAndUploadSharedFilesThunk, publicLinkFilesActions } from "features/public-link-files/model/slice/publicLinkFilesSlice"
import { useSearchParams } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { getPreviewLink, mapFileItemsToTableRow } from "shared/utils/filesUtils"
import { styled } from "styled-components"
import { filesPreviewActions } from "features/files-preview/model/slice/filesPreviewSlice"

import {
  getFilesPreviewIsPublicLinkSelector,
  getFilesPreviewItemsSelector,
  getFilesPreviewOverlayImageDataSelector,
  getFilesPreviewTabsContentLoadedSelector
} from "../model/selectors/filesPreviewSelectors"

import { TabContentLoader } from "./TabContentLoader"

const filterObject = (obj: Record<string, any>) => {
  const newObj = {}

  Object.keys(obj).forEach((key) => {
    if (obj[key] !== undefined) {
      newObj[key] = obj[key]
    }
  })

  return newObj
}

export const VersionsTabContent = ({ currentIndex }: { currentIndex: number }) => {
  const dispatch = useAppDispatch()
  const items = useAppSelector(getFilesPreviewItemsSelector)
  const versions = useAppSelector(getFileVersionsItemsSelector)
  const contentLoaded = useAppSelector(getFilesPreviewTabsContentLoadedSelector)
  const { isUploadEnabled, isEditContentEnabled } = useGetEnablePermission()
  const overlayImageData = useAppSelector(getFilesPreviewOverlayImageDataSelector)
  const [searchParams] = useSearchParams()
  const memoizedVersions = useMemo(() => mapFileItemsToTableRow(versions), [versions])

  const [selectedVersion, setSelectedVersion] = useState<string | null>(null)
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())

  // Сбрасываем состояние неудачных изображений при изменении версий
  useEffect(() => {
    setFailedImages(new Set())
  }, [memoizedVersions])
  const uploadRef = useRef<HTMLLabelElement>(null)

  const currentItem = items[currentIndex] as any

  const handleImageError = (versionId: string) => {
    setFailedImages(prev => new Set(prev).add(versionId))
  }

  const isRestoringInProgressRef = useRef(false)

  const handleUploadBtnClick = () => {
    uploadRef.current.click()
  }

  const isPublicLink = useAppSelector(getFilesPreviewIsPublicLinkSelector)

  useEffect(() => {
    if (isPublicLink) {
      dispatch(fetchSharedFileVersionsThunk({ nodeId: currentItem.id }))
    } else {
      dispatch(fetchFileVersionsThunk({ nodeId: currentItem.id }))
    }

    return () => {
      dispatch(fileVersionsActions.resetState())
    }
  }, [currentItem.id, isPublicLink])

  useEffect(() => {
    setSelectedVersion(memoizedVersions[0]?.id)
  }, [memoizedVersions])

  useEffect(() => {
    if (!overlayImageData?.url) {
      setSelectedVersion(memoizedVersions[0]?.id)
    }
  }, [overlayImageData?.url])

  useEffect(() => {
    const actualVersion = memoizedVersions[0]
    if (actualVersion && memoizedVersions.length > 1 && currentItem?._embedded?.previews?.url_template !== actualVersion?._embedded?.previews?.url_template && isRestoringInProgressRef.current) {
      dispatch(filesPreviewActions.updateItem({ ...filterObject(actualVersion), id: currentItem.id }))

      if (isPublicLink) {
        dispatch(publicLinkFilesActions.updateItem({ ...filterObject(actualVersion), id: currentItem.id }))
      } else {
        dispatch(assetsActions.updateAsset({ ...filterObject(actualVersion), id: currentItem.id }))
      }

      isRestoringInProgressRef.current = false
    }
  }, [memoizedVersions])

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    isRestoringInProgressRef.current = true

    if (isPublicLink) {
      dispatch(checkAndUploadSharedFilesThunk({
        files,
        modificators: [{ target_node_id: currentItem.id }],
        uploadingSourceType: UPLOADER_LIST_TYPES.VERSIONS,
      }))
    } else {
      dispatch(uploadFilesThunk({
        files: files,
        symlinkParentId: undefined,
        modificators: [{ target_node_id: currentItem.linked_node_id || currentItem.id }],
        uploadingSourceType: UPLOADER_LIST_TYPES.VERSIONS
      }))
    }
  }

  const handleRestore = () => {
    isRestoringInProgressRef.current = true

    if (isPublicLink) {
      dispatch(restoreSharedFileVersionThunk({ currentItemId: currentItem.id, versionId: selectedVersion, callback: () => {
        dispatch(filesPreviewActions.clearOverlayImageData())
      } }))
    } else {
      dispatch(restoreFileVersionThunk({ currentItemId: currentItem.id, versionId: selectedVersion, callback: () => {
        dispatch(filesPreviewActions.clearOverlayImageData())
      } }))
    }
  }

  const handleVersionPreview = (version: any) => {
    setSelectedVersion(version.id)

    if (memoizedVersions.length <= 1) {
      return
    }

    if (version.id === memoizedVersions[0]?.id) {
      dispatch(filesPreviewActions.clearOverlayImageData())
      return
    }

    const previewUrl = getPreviewLink(version)

    if (previewUrl) {
      dispatch(filesPreviewActions.setOverlayImageData({
        url: previewUrl,
        type: 'version',
        versionId: version.id,
        versionData: {
          ...version,
          file_info: {
            type: version.file_info?.type || version.type,
            mime: version.file_info?.mime || version.mime,
            ...version.file_info
          }
        }
      }))
    }
  }

  if (!contentLoaded) {
    return <TabContentLoader />
  }

  return (
    <SVersionsTabContent>
      <SVersionsTable>
        {memoizedVersions.map((version) => (
          <SVersionItem key={version.id}>
            <SVersionPreview onClick={() => handleVersionPreview(version)}>
              {!!version.thumbnailLink && !failedImages.has(version.id) ? (
                <SVersionImg
                  src={version.thumbnailLink}
                  draggable={false}
                  onError={() => handleImageError(version.id)}
                />
              ) : (
                <SFileIcon />
              )}

              <SVersionInfo>
                <SVersionDate>
                  {version.modified}
                </SVersionDate>

                <SVersionSize>
                  {version.size}
                </SVersionSize>
              </SVersionInfo>
            </SVersionPreview>

            <SRadio
              checked={selectedVersion === version.id}
              onChange={() => setSelectedVersion(version.id)}
            />
          </SVersionItem>
        ))}
      </SVersionsTable>

      <label
        htmlFor="upload-btn"
        ref={uploadRef}
      >
        <SHiddenFilesInput
          id="upload-btn"
          onChange={handleUpload}
          type="file"
          multiple
        />
      </label>

      <SRestoreButton
        type="primary"
        onClick={handleRestore}
        disabled={!selectedVersion || memoizedVersions[0]?.id === selectedVersion}
      >
        Revert to this version
      </SRestoreButton>

      {(isUploadEnabled || isEditContentEnabled) && (
        <SUploadButton
          color="primary"
          variant="outlined"
          onClick={handleUploadBtnClick}
        >
        Upload new version
        </SUploadButton>
      )}
    </SVersionsTabContent>
  )
}

const SVersionsTabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 24px;
  height: calc(100vh - 164px);
`

const SVersionsTable = styled.div`
  width: 100%;
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 300px);

  @supports (-moz-appearance: none) { 
    scrollbar-color: var(--scroll-color) transparent; 
  }
  
  &::-webkit-scrollbar {
    width: 4px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--scroll-color);
    border-radius: 14px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`


const SVersionItem = styled.div`
  display: flex;
  align-items: center;
  height: 80px;

  & ~ & {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
`

const SVersionPreview = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  cursor: pointer;
`

const SVersionImg = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
`

const SVersionInfo = styled.div`
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const SVersionDate = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1;
`

const SVersionSize = styled.div`
  margin-top: 4px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1;
`

const SRadio = styled(Radio)`
  margin-left: auto;
`

const SHiddenFilesInput = styled.input`
  display: none;
`

const SRestoreButton = styled(Button)`
  margin-top: auto;
  height: 32px;
  box-shadow: none !important;
  
  &:hover {
    background-color: var(--ant-color-primary-hover) !important;
    box-shadow: none !important;
  }
  
  &:focus {
    box-shadow: none !important;
  }
  
  &:active {
    box-shadow: none !important;
  }
`

const SUploadButton = styled(Button)`
  height: 32px;
  box-shadow: none !important;
  
  &:hover {
    box-shadow: none !important;
  }
  
  &:focus {
    box-shadow: none !important;
  }
  
  &:active {
    box-shadow: none !important;
  }
`

const SFileIcon = styled(FileOutlined)`
  font-size: 48px;
  color: rgba(255, 255, 255, 0.45);
`

